import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import type { Market, Ticker } from "@/types";
import { formatChange, formatPrice, formatQty } from "@/utils/format";
import { MarketDropdown } from "./market-dropdown";

type MarketHeaderProps = {
	symbol: string;
	markets: Market[];
	tickers: Record<string, Ticker>;
};

export function MarketHeader({ symbol, markets, tickers }: MarketHeaderProps) {
	const market = markets.find((m) => m.symbol === symbol);
	const ticker = tickers[symbol];
	const base = market?.baseAsset ?? symbol.split("_")[0] ?? symbol;
	const quote = market?.quoteAsset ?? symbol.split("_")[1] ?? "USD";
	const currentChange = formatChange(ticker?.priceChangePercent);
	const [priceDirection, setPriceDirection] = useState<"up" | "down">("up");
	const prevLastPriceRef = useRef<string | null>(null);

	useEffect(() => {
		const prev = prevLastPriceRef.current;
		prevLastPriceRef.current = ticker?.lastPrice ?? null;
		if (prev == null || ticker?.lastPrice == null) {
			setPriceDirection("up");
			return;
		}
		const cur = Number(ticker.lastPrice);
		const old = Number(prev);
		if (!Number.isFinite(cur) || !Number.isFinite(old) || cur === old || cur > old) {
			setPriceDirection("up");
		} else {
			setPriceDirection("down");
		}
	}, [ticker?.lastPrice]);

	return (
		<div className="flex flex-wrap items-center gap-4 rounded-xl border border-border/60 bg-l1 px-3 py-3 shadow-sm sm:px-5">
			<div className="flex w-full min-w-0 items-center gap-4 sm:gap-6">
				<MarketDropdown
					symbol={symbol}
					base={base}
					quote={quote}
					markets={markets}
					tickers={tickers}
				/>
				<div className="ml-auto flex shrink-0 flex-col items-end gap-0.5 sm:hidden">
					<span
						className={cn(
							"text-base font-bold",
							priceDirection === "up" ? "text-green-text" : "text-red-text",
						)}
					>
						{formatPrice(ticker?.lastPrice)}
					</span>
					<span
						className={cn(
							"text-xs font-medium",
							currentChange.isUp ? "text-green-text" : "text-red-text",
						)}
					>
						{currentChange.text}
					</span>
				</div>

				<div className="hidden sm:block h-8 w-px bg-border" />
				<div className="hidden min-w-0 flex-1 flex-col gap-0.5 sm:flex">
					<div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-xs xl:gap-x-8">
						<div>
							<span
								className={cn(
									"text-lg font-bold",
									priceDirection === "up" ? "text-green-text" : "text-red-text",
								)}
							>
								{formatPrice(ticker?.lastPrice)}
							</span>
						</div>
						<div className="flex flex-col gap-1">
							<span className="text-muted-foreground text-[10px]">24h Change</span>
							<span
								className={cn(
									"font-medium",
									currentChange.isUp ? "text-green-text" : "text-red-text",
								)}
							>
								{formatPrice(ticker?.priceChange)} {currentChange.text}
							</span>
						</div>
						<div className="flex flex-col gap-1">
							<span className="text-muted-foreground text-[10px]">24h High</span>
							<span className="font-medium text-high-emphasis">{formatPrice(ticker?.high)}</span>
						</div>
						<div className="flex flex-col gap-1">
							<span className="text-muted-foreground text-[10px]">24h Low</span>
							<span className="font-medium text-high-emphasis">{formatPrice(ticker?.low)}</span>
						</div>
						<div className="flex flex-col gap-1">
							<span className="text-muted-foreground text-[10px]">24h Vol ({base})</span>
							<span className="font-medium text-high-emphasis">{formatQty(ticker?.volume, 2)}</span>
						</div>
						<div className="flex flex-col gap-1 text-[10px]">
							<span className="text-muted-foreground">24h Vol ({quote})</span>
							<span className="font-medium text-high-emphasis">
								{formatQty(ticker?.quoteVolume, 2)}
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
